import { ActionTree } from "vuex";
import { IStoreRootState } from "@/store/interface";
import { ForexBuyState } from "@/feature/Forex/store/buy/interface";
import { FileListItem } from "@/components/IBFile/interface";
import {
	$canSendForexDraftToBank,
	$createForexDraft,
	$deleteForexFile,
	$sendForexDraftToAccountant,
	$sendForexDraftToDirector,
	$updateForexDraft,
} from "@/feature/Forex/api/form/forex-form";
import { ForexBuyAction, ForexBuyMutation } from "@/feature/Forex/store/buy/constants";
import {
	createForexBuyUploadFileSource,
	deleteForexFileAttachment,
	getForexDraft,
	getForexDraftWithId,
	sendAndSaveForexDraft
} from "@/feature/Forex/store/buy/utils";
import { ForexBuyDraft } from "@/feature/Forex/api/form/forex-form.interface";
import { $deleteForexDraft } from "@/feature/Forex/api/draft/forex-draft";

export default <ActionTree<ForexBuyState, IStoreRootState>> {

	async [ForexBuyAction.deleteFile]({ state }, payload: FileListItem) {
		if (state.uploadFileRequests[payload.fileId])
			return state.uploadFileRequests[payload.fileId]();

		const result = await $deleteForexFile(payload.id);

		result && deleteForexFileAttachment(state, payload.id);

		return result;
	},

	async [ForexBuyAction.deleteDraft]({ state, commit }) {
		if (!state.currentDraft.foreignExchangeId)
			return false;

		const result = await $deleteForexDraft(state.currentDraft.foreignExchangeId);

		result && commit(ForexBuyMutation.resetCurrentDraft);

		return result;
	},

	async [ForexBuyAction.uploadFile]({ state, dispatch }, payload: FileListItem) {
		if (!state.currentDraft.foreignExchangeId)
			await dispatch(ForexBuyAction.createDraft);

		const uploadSource = createForexBuyUploadFileSource(state, payload);
		const uploadResponseDTO = await uploadSource.request;

		if (uploadResponseDTO) {
			payload.id = uploadResponseDTO?.documentId as string;

			state.currentDraft?.fileAttachments?.push(uploadResponseDTO);
		}

		delete state.uploadFileRequests[payload.fileId];

		return uploadResponseDTO;
	},

	async [ForexBuyAction.createDraft]({ state, commit }, payload?: ForexBuyDraft) {
		if (state.currentDraftRequest) return state.currentDraftRequest;

		state.currentDraftRequest = $createForexDraft(getForexDraft(state, payload));

		const draft = await state.currentDraftRequest;

		state.currentDraftRequest = null;

		if (draft) commit(ForexBuyMutation.setCurrentDraft, draft);

		return draft;
	},

	async [ForexBuyAction.updateDraft]({ state, commit }, payload: ForexBuyDraft) {
		const draft = await $updateForexDraft({
			...getForexDraft(state, payload),
			foreignExchangeId: state.currentDraft.foreignExchangeId
		});

		if (draft) commit(ForexBuyMutation.setCurrentDraft, draft);

		return draft;
	},

	async [ForexBuyAction.createOrUpdateDraft]({ state, dispatch }, payload: ForexBuyDraft) {
		return dispatch(
			(state.currentDraft.foreignExchangeId && !state.draftIsSent)
				? ForexBuyAction.updateDraft
				: ForexBuyAction.createDraft,
			payload
		);
	},

	async [ForexBuyAction.sendDraftToAccountant]({ state, commit }, payload: ForexBuyDraft) {
		return sendAndSaveForexDraft(
			state,
			payload,
			commit,
			$sendForexDraftToAccountant,
		);
	},

	async [ForexBuyAction.sendDraftToDirector]({ state, commit }, payload: ForexBuyDraft) {
		return sendAndSaveForexDraft(
			state,
			payload,
			commit,
			$sendForexDraftToDirector,
		);
	},

	async [ForexBuyAction.canSendDraftToDirector]({ state, commit }, payload: ForexBuyDraft) {
		const result = $canSendForexDraftToBank(
			getForexDraftWithId(state, payload)
		);

		commit(ForexBuyMutation.setDraftIsSent, true);

		return result;
	},

}